import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle, Send, Globe, Home, Building } from 'lucide-react';

interface ScopeInfo {
  type: 'global' | 'unit' | 'department';
  name?: string;
}

interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (content: string) => Promise<void>;
  customerName?: string;
  /**
   * Scope information for displaying in the modal title
   * - global: Note will be visible to all (no unit/department)
   * - unit: Note is specific to a unit (shows unit name)
   * - department: Note is specific to a department (shows department name)
   */
  scopeInfo?: ScopeInfo;
}

/**
 * Modal for creating a new customer note
 * Now shows scope information in the title (Global, Unit, or Department)
 */
export const AddNoteModal: React.FC<AddNoteModalProps> = ({
  open,
  onClose,
  onSubmit,
  customerName,
  scopeInfo,
}) => {
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setContent('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      setError('O conteúdo do aviso não pode estar vazio');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(trimmedContent);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao criar aviso');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !isSubmitting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose, isSubmitting]);

  // Handle Ctrl+Enter to submit
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!open) return null;

  // Get scope display info
  const getScopeDisplay = () => {
    if (!scopeInfo || scopeInfo.type === 'global') {
      return {
        icon: Globe,
        label: 'Aviso Global',
        description: 'Este aviso será visível para todos os operadores visualizando este cliente.',
        bgColor: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
      };
    }
    if (scopeInfo.type === 'unit') {
      return {
        icon: Home,
        label: `Aviso da Unidade${scopeInfo.name ? `: ${scopeInfo.name}` : ''}`,
        description: `Este aviso será visível apenas ao visualizar a unidade ${scopeInfo.name || 'selecionada'}.`,
        bgColor: 'bg-orange-500/20',
        iconColor: 'text-orange-400',
      };
    }
    return {
      icon: Building,
      label: `Aviso do Departamento${scopeInfo.name ? `: ${scopeInfo.name}` : ''}`,
      description: `Este aviso será visível apenas ao visualizar o departamento ${scopeInfo.name || 'selecionado'}.`,
      bgColor: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
    };
  };

  const scopeDisplay = getScopeDisplay();
  const ScopeIcon = scopeDisplay.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gray-900 rounded-xl shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${scopeDisplay.bgColor} rounded-lg`}>
              <AlertTriangle className={`h-5 w-5 ${scopeDisplay.iconColor}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Novo Aviso</h2>
              {customerName && (
                <p className="text-sm text-gray-400">{customerName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scope Indicator */}
        <div className={`flex items-center gap-2 mx-4 mt-4 p-3 ${scopeDisplay.bgColor} rounded-lg border border-gray-700`}>
          <ScopeIcon className={`h-4 w-4 ${scopeDisplay.iconColor}`} />
          <span className={`text-sm font-medium ${scopeDisplay.iconColor}`}>
            {scopeDisplay.label}
          </span>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-4">
            <label htmlFor="note-content" className="block text-sm font-medium text-gray-300 mb-2">
              Conteúdo do Aviso
            </label>
            <textarea
              ref={textareaRef}
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite o aviso para os operadores..."
              rows={4}
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            />
            <p className="mt-2 text-xs text-gray-500">
              {scopeDisplay.description}
              <br />
              <span className="text-gray-400">Dica: Ctrl+Enter para enviar</span>
            </p>

            {error && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !content.trim()}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Salvar Aviso
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNoteModal;
